/**
 * Repository for patient-centric admin endpoints used by the field-setup wizard.
 *
 * These endpoints live on a separate `patient-centric-app-v2` backend (NOT the
 * Amplify-fronted `apiName` API). They're authorised by a Cognito idToken
 * carried as `Authorization: Bearer <token>`. The backend gates by Cognito
 * group membership (callers must be in `setup_app_user`).
 */

function getPatientCentricBaseUrl(): string {
  const env = (import.meta as unknown as { env?: Record<string, string> }).env;
  const base = env?.VITE_API_GATEWAY_URL_V2 ?? "";
  // Prod / dev have a reverse proxy that mounts the FastAPI app under
  // `/patient-centric-app-v2`. A local Docker backend hits FastAPI
  // directly with no proxy, so the prefix would 404. Set
  // VITE_SKIP_API_PREFIX=true when pointing at localhost:8090.
  const skipPrefix = env?.VITE_SKIP_API_PREFIX === "true";
  const suffix = skipPrefix ? "" : "/patient-centric-app-v2";
  return (base + suffix).replace(/\/$/, "");
}

export interface AdminClinicListItem {
  id: number;
  name: string;
  address?: string;
  contact?: string;
}

export interface CreatePatientAppUserBody {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  dob: string;
  gender: string;
  is_otp_enabled: boolean;
  weight_kg: string;
  height_cm: string;
  user_type: string;
  is_cpro_app_member: boolean;
  clinic_ids: number[];
  contact: string;
  clinic_id: number;
  use_phone_as_username: boolean;
  /**
   * When `true`, the backend skips emailing/SMS-ing the welcome invite
   * to the new member. The technician is then responsible for sharing
   * the sign-in details out-of-band. When `false` (the default UX),
   * the backend dispatches the invite as usual.
   */
  no_invitation: boolean;
  /**
   * Bypass the v2 duplicate check and create a fresh patient row even
   * when a `(clinic_id, first_name, last_name, dob)` match already
   * exists. Sent ONLY after the technician has explicitly chosen
   * "Create anyway" in the 409-A duplicate dialog — never on the first
   * attempt. Omitted from the wire payload when undefined/false to keep
   * legacy v1 callers and other consumers byte-compatible.
   *
   * The backend defaults this to `false`. On the v2 replace endpoint
   * the field is accepted (since `ReplacePatientAppUserBody` inherits
   * from `CreatePatientAppUserBody`) but ignored, because the patient
   * already exists.
   */
  force_create?: boolean;
}

/**
 * Body for the v2 create endpoint. Same as v1 plus the technician's
 * local "today" as `date_of_admission` (YYYY-MM-DD). Computed at submit
 * time, not at form-mount time, so leaving the screen open past midnight
 * doesn't yield a stale admission date.
 */
export interface CreatePatientAppUserV2Body extends CreatePatientAppUserBody {
  date_of_admission: string;
}

/**
 * Body for the v2 replace endpoint. Identical to the v2 create body
 * plus the `clinic_patient_id` of the existing patient that was
 * surfaced in the 409-A response.
 */
export interface ReplacePatientAppUserV2Body
  extends CreatePatientAppUserV2Body {
  clinic_patient_id: number;
}

export interface CreatePatientAppUserResult {
  clinicPatientId: number;
  admissionId: number;
  userId: string;
}

/**
 * The "already paired" device the backend surfaces inside a successful
 * /v2/replace response when the existing patient already had a device
 * assigned. The frontend uses this to skip Step 2 (Device) of the
 * setup wizard and hand the technician straight to verification or
 * handover.
 *
 * Mirrors the shape from `GET /clinic/{id}/patient/{id}/device` so
 * downstream screens (Verify, Handover, Complete) can render without
 * an extra round-trip. `floor_id` is included because some clinics
 * key by floor when showing room labels.
 */
export interface PairedDevice {
  device_id: string;
  name: string;
  floor_id: number | null;
  clinic_id: number;
}

/**
 * Result of a successful replace. `admissionId` was previously omitted
 * by the backend (the existing admission record is preserved) — it's
 * now echoed back when present, so the type stays optional for
 * forward-compat.
 *
 * `deviceAlreadyPaired` + `pairedDevice` were added when the backend
 * started inspecting the existing patient for a paired device. When
 * `deviceAlreadyPaired === true` the UI skips Step 2 of the wizard
 * and routes the tech directly to verification / handover; the
 * matching `pairedDevice` is dispatched into `state.setup.device` so
 * downstream screens render the right hardware identity.
 */
export interface ReplacePatientAppUserResult {
  clinicPatientId: number;
  userId: string;
  admissionId?: number;
  deviceAlreadyPaired: boolean;
  pairedDevice: PairedDevice | null;
}

/**
 * Which backend dedupe lane surfaced an `ExistingPatient`:
 *   - "name_dob" → the original 409-A check (clinic + first + last + DOB).
 *   - "email"    → a 409-B email collision upgraded to 409-A because the
 *                  email's owner is a patient of the technician's clinic.
 *   - "phone"    → same as "email" but for the use-phone-as-username path.
 */
export type ExistingPatientMatchedBy = "name_dob" | "email" | "phone";

/**
 * "Existing patient" payload that v2 returns inside a 409 body when a
 * duplicate is detected. The UI uses this to populate the
 * duplicate-resolution dialog.
 *
 * The first block of fields has been part of the contract since 409-A
 * shipped. The trailing block is additive — older backends omit them,
 * so every one is optional and the parser tolerates their absence.
 */
export interface ExistingPatient {
  clinic_patient_id: number;
  first_name: string;
  last_name: string;
  dob: string;
  clinic_id: number;
  existing_email: string | null;
  /**
   * Integer `users.id` PK of the existing patient's account — the value
   * the `/setup/users/:userId` route expects (NOT the Cognito sub).
   * Null/undefined when the backend couldn't resolve a linked users row.
   */
  user_id?: number | null;
  /** Which dedupe lane matched. Absent on older backends → treat as "name_dob". */
  matched_by?: ExistingPatientMatchedBy;
  /** Last 4 digits of the existing account's phone, when known. */
  phone_last4?: string | null;
  /** ISO timestamp the existing patient was created, when known. */
  created_at?: string | null;
  /** Display name of whoever created the existing patient, when known. */
  created_by_name?: string | null;
  /** Whether the existing patient already has a device paired. */
  has_device?: boolean;
}

/**
 * Outcome of a v2 create attempt. Discriminated so the UI can react
 * to each case without parsing HTTP shapes or string-matching error
 * messages.
 *
 *   created           → 201, patient is in the database.
 *   duplicate-patient → 409 with `existing_patient` (a real patient
 *                       with the same name+DOB+clinic exists).
 *   username-conflict → 409 without `existing_patient` (the Cognito
 *                       username — email or phone — is taken).
 *   error             → everything else (4xx that isn't 409, 5xx,
 *                       network failure, auth failure).
 */
export type CreatePatientAppUserV2Outcome =
  | { kind: "created"; data: CreatePatientAppUserResult }
  | { kind: "duplicate-patient"; existing: ExistingPatient }
  | { kind: "username-conflict"; message: string }
  | { kind: "error"; error: PatientCentricApiError };

/**
 * Outcome of a v2 replace attempt. The spec surfaces three meaningful
 * client-side error shapes (email-in-use, phone-in-use, 404) plus the
 * username-conflict carry-over from create. Everything else falls
 * through to `error`.
 */
export type ReplacePatientAppUserV2Outcome =
  | { kind: "replaced"; data: ReplacePatientAppUserResult }
  | { kind: "username-conflict"; message: string }
  | { kind: "field-error"; field: "email" | "phone"; message: string }
  | { kind: "not-found"; message: string }
  | { kind: "error"; error: PatientCentricApiError };

/**
 * Body for linking a *family member* login to an existing patient. Hits the
 * same `POST /patient-app/users/v2` endpoint as a normal create, but the
 * presence of `link_to_patient_id` tells the backend to attach the new
 * Cognito account to that patient (sharing its patient_id / clinic_id at
 * login) instead of creating a fresh clinic_patient record.
 *
 * `gender` is intentionally omitted — the backend's GenderType enum only
 * accepts "male" / "female", and family members don't provide it in this
 * flow, so we leave the optional field unset rather than send a placeholder.
 */
export interface InviteFamilyMemberBody {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  dob: string;
  is_otp_enabled: boolean;
  weight_kg: string;
  height_cm: string;
  user_type: string;
  is_cpro_app_member: boolean;
  clinic_ids: number[];
  contact: string;
  clinic_id: number;
  use_phone_as_username: boolean;
  no_invitation: boolean;
  date_of_admission: string;
  link_to_patient_id: number;
}

/**
 * Outcome of a family-member invite attempt. Discriminated so the UI can
 * react without parsing HTTP shapes.
 *
 *   invited           → 201, the account was linked to the patient.
 *   limit-reached     → 409 whose message mentions the 2-member maximum.
 *   username-conflict → any other 409 (email/phone already registered, or a
 *                       same-clinic patient already owns the email).
 *   error             → everything else (non-409 4xx, 5xx, network, auth).
 */
export type InviteFamilyMemberOutcome =
  | { kind: "invited"; clinicPatientId: number }
  | { kind: "limit-reached"; message: string }
  | { kind: "username-conflict"; message: string }
  | { kind: "error"; error: PatientCentricApiError };

/**
 * A family member linked to a patient, as returned by
 * `GET /patient-app/patients/{patient_id}/family-members`. The backend
 * resolves each linked Cognito sub to the user's name/email; `relation`
 * is not persisted server-side so it is not part of this shape.
 */
export interface LinkedFamilyMember {
  id: string;
  name: string;
  email: string;
}

export interface AssignDeviceToPatientBody {
  device_id: string;
  name: string;
  status: number;
}

export class PatientCentricApiError extends Error {
  status: number;
  body: unknown;

  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.name = "PatientCentricApiError";
    this.status = status;
    this.body = body;
  }
}

async function parseJsonSafe(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

function extractMessage(body: unknown, fallback: string): string {
  if (
    typeof body === "object" &&
    body !== null &&
    "message" in body &&
    typeof (body as { message: unknown }).message === "string"
  ) {
    return (body as { message: string }).message;
  }
  return fallback;
}

/**
 * Extract a normalised `ExistingPatient` from a 409-A body if and only
 * if it looks well-formed. Returning `null` lets the caller treat the
 * 409 as a username-conflict (409-B) — safest fallback when shapes drift.
 */
function parseExistingPatient(body: unknown): ExistingPatient | null {
  if (typeof body !== "object" || body === null) return null;
  const ep = (body as { existing_patient?: unknown }).existing_patient;
  if (typeof ep !== "object" || ep === null) return null;
  const obj = ep as Record<string, unknown>;
  const clinicPatientId = obj.clinic_patient_id;
  const firstName = obj.first_name;
  const lastName = obj.last_name;
  const dob = obj.dob;
  const clinicId = obj.clinic_id;
  const existingEmail = obj.existing_email;
  if (
    typeof clinicPatientId !== "number" ||
    typeof firstName !== "string" ||
    typeof lastName !== "string" ||
    typeof dob !== "string" ||
    typeof clinicId !== "number"
  ) {
    return null;
  }

  // Additive fields — present only on newer backends. Each is validated
  // independently so a missing/oddly-typed value degrades to undefined
  // rather than rejecting the whole payload (which would drop us back to
  // the 409-B username-conflict fallback and lose the dialog entirely).
  const rawUserId = obj.user_id;
  const userId =
    typeof rawUserId === "number"
      ? rawUserId
      : rawUserId === null
      ? null
      : undefined;

  const rawMatchedBy = obj.matched_by;
  const matchedBy: ExistingPatientMatchedBy | undefined =
    rawMatchedBy === "name_dob" ||
    rawMatchedBy === "email" ||
    rawMatchedBy === "phone"
      ? rawMatchedBy
      : undefined;

  const rawPhoneLast4 = obj.phone_last4;
  const phoneLast4 =
    typeof rawPhoneLast4 === "string"
      ? rawPhoneLast4
      : rawPhoneLast4 === null
      ? null
      : undefined;

  const rawCreatedAt = obj.created_at;
  const createdAt =
    typeof rawCreatedAt === "string"
      ? rawCreatedAt
      : rawCreatedAt === null
      ? null
      : undefined;

  const rawCreatedByName = obj.created_by_name;
  const createdByName =
    typeof rawCreatedByName === "string"
      ? rawCreatedByName
      : rawCreatedByName === null
      ? null
      : undefined;

  const rawHasDevice = obj.has_device;
  const hasDevice =
    typeof rawHasDevice === "boolean" ? rawHasDevice : undefined;

  return {
    clinic_patient_id: clinicPatientId,
    first_name: firstName,
    last_name: lastName,
    dob,
    clinic_id: clinicId,
    existing_email:
      typeof existingEmail === "string"
        ? existingEmail
        : existingEmail === null
        ? null
        : null,
    user_id: userId,
    matched_by: matchedBy,
    phone_last4: phoneLast4,
    created_at: createdAt,
    created_by_name: createdByName,
    has_device: hasDevice,
  };
}

/**
 * Both v2 endpoints return data nested under `data: { ... }`. The user
 * row may sit at `data.user` (replace) or sometimes inline depending on
 * the backend, so we look in both places. `clinic_patient_id` always
 * lives on `data`.
 */
function parseUserIdFromV2Body(body: unknown): string {
  if (typeof body !== "object" || body === null) return "";
  const data = (body as { data?: unknown }).data;
  if (typeof data !== "object" || data === null) return "";
  const dataObj = data as Record<string, unknown>;
  const user = dataObj.user as Record<string, unknown> | undefined;
  if (user && typeof user.user_id === "string") return user.user_id;
  if (typeof dataObj.user_id === "string") return dataObj.user_id;
  return "";
}

function parseClinicPatientIdFromV2Body(body: unknown): number | null {
  if (typeof body !== "object" || body === null) return null;
  const data = (body as { data?: unknown }).data;
  if (typeof data !== "object" || data === null) return null;
  const id = (data as { clinic_patient_id?: unknown }).clinic_patient_id;
  return typeof id === "number" ? id : null;
}

function parseAdmissionIdFromV2Body(body: unknown): number | undefined {
  if (typeof body !== "object" || body === null) return undefined;
  const data = (body as { data?: unknown }).data;
  if (typeof data !== "object" || data === null) return undefined;
  const id = (data as { admission_id?: unknown }).admission_id;
  return typeof id === "number" ? id : undefined;
}

/**
 * Parse `data.device_already_paired` from a v2 replace response,
 * defaulting to `false` if the field is missing or non-boolean. The
 * default is intentionally conservative — we'd rather make the
 * technician walk through Step 2 unnecessarily than silently skip the
 * device-assignment step for a patient who in fact has no device.
 */
function parseDeviceAlreadyPairedFromV2Body(body: unknown): boolean {
  if (typeof body !== "object" || body === null) return false;
  const data = (body as { data?: unknown }).data;
  if (typeof data !== "object" || data === null) return false;
  const flag = (data as { device_already_paired?: unknown })
    .device_already_paired;
  return flag === true;
}

/**
 * Parse `data.paired_device` from a v2 replace response. Returns
 * `null` when the field is missing, explicitly null, or not
 * well-formed.
 *
 * Treating partial / malformed objects as null is paired with the
 * conservative `parseDeviceAlreadyPairedFromV2Body` default — the
 * combined effect is "skip Step 2 only when BOTH the flag is true AND
 * we have a well-formed device payload to dispatch into state". The
 * useHook layer enforces this AND.
 */
function parsePairedDeviceFromV2Body(body: unknown): PairedDevice | null {
  if (typeof body !== "object" || body === null) return null;
  const data = (body as { data?: unknown }).data;
  if (typeof data !== "object" || data === null) return null;
  const pd = (data as { paired_device?: unknown }).paired_device;
  if (typeof pd !== "object" || pd === null) return null;
  const obj = pd as Record<string, unknown>;
  const deviceId = obj.device_id;
  const name = obj.name;
  const clinicId = obj.clinic_id;
  if (typeof deviceId !== "string" || !deviceId) return null;
  if (typeof name !== "string") return null;
  if (typeof clinicId !== "number") return null;
  return {
    device_id: deviceId,
    name,
    floor_id: typeof obj.floor_id === "number" ? obj.floor_id : null,
    clinic_id: clinicId,
  };
}

export type ISetupRepository = ReturnType<typeof SetupRepositoryFactory>;

export const SetupRepositoryFactory = ({
  AuthService,
}: {
  AuthService: IAuthService;
}) => {
  const { getAuthenticationToken } = AuthService;

  const fetchAdminClinicsAPI = async (): Promise<AdminClinicListItem[]> => {
    const token = await getAuthenticationToken();
    if (!token) throw new PatientCentricApiError("No auth token", 401, null);
    const url = `${getPatientCentricBaseUrl()}/clinic/my-clinic-slim`;
    const res = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
    });
    const body = await parseJsonSafe(res);
    if (!res.ok) {
      throw new PatientCentricApiError(
        extractMessage(body, `HTTP ${res.status}`),
        res.status,
        body
      );
    }
    if (typeof body === "object" && body !== null) {
      return body as AdminClinicListItem[];
    }
    return [];
  };

  const createPatientAppUserAPI = async (
    input: CreatePatientAppUserBody
  ): Promise<CreatePatientAppUserResult> => {
    const token = await getAuthenticationToken();
    if (!token) throw new PatientCentricApiError("No auth token", 401, null);
    const url = `${getPatientCentricBaseUrl()}/patient-app/users`;
    const res = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(input),
    });
    const body = await parseJsonSafe(res);
    if (!res.ok) {
      throw new PatientCentricApiError(
        extractMessage(body, `HTTP ${res.status}`),
        res.status,
        body
      );
    }
    const data =
      typeof body === "object" &&
      body !== null &&
      "data" in body &&
      (body as { data: unknown }).data
        ? (body as { data: Record<string, unknown> }).data
        : null;
    if (!data || typeof data !== "object") {
      throw new PatientCentricApiError(
        "Unexpected response shape",
        res.status,
        body
      );
    }
    const clinicPatientId = data.clinic_patient_id;
    const admissionId = data.admission_id;
    const user = data.user as Record<string, unknown> | undefined;
    const userId = user && typeof user.user_id === "string" ? user.user_id : "";

    if (
      typeof clinicPatientId !== "number" ||
      typeof admissionId !== "number"
    ) {
      throw new PatientCentricApiError(
        "Missing clinic_patient_id or admission_id",
        res.status,
        body
      );
    }
    return { clinicPatientId, admissionId, userId };
  };

  /**
   * v2 create. Same payload contract as v1 plus `date_of_admission`.
   * Returns a discriminated outcome so callers don't need to inspect
   * HTTP status / body shape themselves.
   *
   * 409 detection contract (see backend spec):
   *   - 409 + body.existing_patient present → 409-A → duplicate-patient
   *   - 409 + no existing_patient           → 409-B → username-conflict
   *
   * Errors (network, 4xx other than 409, 5xx) are funneled into the
   * `error` branch — the existing controller-layer error handling
   * (Mixpanel via `analyticsController.logApiError`) is preserved.
   */
  const createPatientAppUserV2API = async (
    input: CreatePatientAppUserV2Body
  ): Promise<CreatePatientAppUserV2Outcome> => {
    let res: Response;
    let body: unknown;
    try {
      const token = await getAuthenticationToken();
      if (!token) {
        return {
          kind: "error",
          error: new PatientCentricApiError("No auth token", 401, null),
        };
      }
      const url = `${getPatientCentricBaseUrl()}/patient-app/users/v2`;
      res = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(input),
      });
      body = await parseJsonSafe(res);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Network error";
      return {
        kind: "error",
        error: new PatientCentricApiError(msg, 0, null),
      };
    }

    if (res.status === 409) {
      const existing = parseExistingPatient(body);
      if (existing) {
        return { kind: "duplicate-patient", existing };
      }
      return {
        kind: "username-conflict",
        message: extractMessage(body, "User account already exists"),
      };
    }

    if (!res.ok) {
      return {
        kind: "error",
        error: new PatientCentricApiError(
          extractMessage(body, `HTTP ${res.status}`),
          res.status,
          body
        ),
      };
    }

    const clinicPatientId = parseClinicPatientIdFromV2Body(body);
    const admissionId = parseAdmissionIdFromV2Body(body);
    if (clinicPatientId === null || typeof admissionId !== "number") {
      return {
        kind: "error",
        error: new PatientCentricApiError(
          "Missing clinic_patient_id or admission_id in v2 create response",
          res.status,
          body
        ),
      };
    }
    return {
      kind: "created",
      data: {
        clinicPatientId,
        admissionId,
        userId: parseUserIdFromV2Body(body),
      },
    };
  };

  /**
   * Replace an existing patient surfaced via 409-A. Takes the same body
   * the v2 create attempt sent plus the `clinic_patient_id` from the
   * `existing_patient` payload. The backend preserves the existing
   * admission record (so the response intentionally omits
   * `admission_id`) and re-issues the user row with the new field values.
   */
  const replacePatientAppUserV2API = async (
    input: ReplacePatientAppUserV2Body
  ): Promise<ReplacePatientAppUserV2Outcome> => {
    let res: Response;
    let body: unknown;
    try {
      const token = await getAuthenticationToken();
      if (!token) {
        return {
          kind: "error",
          error: new PatientCentricApiError("No auth token", 401, null),
        };
      }
      const url = `${getPatientCentricBaseUrl()}/patient-app/users/v2/replace`;
      res = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(input),
      });
      body = await parseJsonSafe(res);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Network error";
      return {
        kind: "error",
        error: new PatientCentricApiError(msg, 0, null),
      };
    }

    const message = extractMessage(body, res.ok ? "" : `HTTP ${res.status}`);

    // Email / phone collisions are surfaced as field-level errors so
    // the duplicate-resolution dialog closes and the technician can
    // correct the offending field inline. The backend uses 409 for
    // these (mirroring the create endpoint's contract), but we keep
    // the 400 branch as a defensive fallback — older deployed backends
    // returned 400 for the same condition and we don't want a
    // mid-rollout deploy to regress this UX.
    if (res.status === 400 || res.status === 409) {
      const lower = message.toLowerCase();
      if (lower.includes("phone")) {
        return { kind: "field-error", field: "phone", message };
      }
      if (lower.includes("email")) {
        return { kind: "field-error", field: "email", message };
      }
      // 409 without an identifiable field falls through to
      // username-conflict so the dialog can surface a generic
      // "already taken" message; 400 without a field is an unexpected
      // shape and lands in the catch-all error branch.
      if (res.status === 409) {
        return { kind: "username-conflict", message };
      }
      return {
        kind: "error",
        error: new PatientCentricApiError(message, res.status, body),
      };
    }

    if (res.status === 404) {
      return { kind: "not-found", message };
    }

    if (!res.ok) {
      return {
        kind: "error",
        error: new PatientCentricApiError(message, res.status, body),
      };
    }

    const clinicPatientId =
      parseClinicPatientIdFromV2Body(body) ?? input.clinic_patient_id;
    const pairedDevice = parsePairedDeviceFromV2Body(body);
    // Insist on BOTH signals: the flag and a well-formed paired_device
    // payload. If the backend ever sends `device_already_paired: true`
    // without a usable device object (or vice versa) we fall back to
    // running the full Step 2 flow — safer than silently skipping
    // device setup for a patient that doesn't have a working device
    // record on file.
    const deviceAlreadyPaired =
      parseDeviceAlreadyPairedFromV2Body(body) && pairedDevice !== null;
    return {
      kind: "replaced",
      data: {
        clinicPatientId,
        userId: parseUserIdFromV2Body(body),
        admissionId: parseAdmissionIdFromV2Body(body),
        deviceAlreadyPaired,
        pairedDevice: deviceAlreadyPaired ? pairedDevice : null,
      },
    };
  };

  /**
   * Invite a family member by linking a new login to an existing patient.
   * Calls the same `POST /patient-app/users/v2` endpoint as create, with
   * `link_to_patient_id` set. Returns a discriminated outcome.
   *
   * 409 disambiguation (see backend create_user_v2.py):
   *   - message mentions "maximum" → the patient already has 2 linked
   *     members → limit-reached.
   *   - any other 409 → the email/phone is already registered (or a
   *     same-clinic patient owns it) → username-conflict.
   */
  const inviteFamilyMemberAPI = async (
    input: InviteFamilyMemberBody
  ): Promise<InviteFamilyMemberOutcome> => {
    let res: Response;
    let body: unknown;
    try {
      const token = await getAuthenticationToken();
      if (!token) {
        return {
          kind: "error",
          error: new PatientCentricApiError("No auth token", 401, null),
        };
      }
      const url = `${getPatientCentricBaseUrl()}/patient-app/users/v2`;
      res = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(input),
      });
      body = await parseJsonSafe(res);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Network error";
      return {
        kind: "error",
        error: new PatientCentricApiError(msg, 0, null),
      };
    }

    if (res.status === 409) {
      const message = extractMessage(body, "Conflict");
      if (message.toLowerCase().includes("maximum")) {
        return { kind: "limit-reached", message };
      }
      return { kind: "username-conflict", message };
    }

    if (!res.ok) {
      return {
        kind: "error",
        error: new PatientCentricApiError(
          extractMessage(body, `HTTP ${res.status}`),
          res.status,
          body
        ),
      };
    }

    const clinicPatientId =
      parseClinicPatientIdFromV2Body(body) ?? input.link_to_patient_id;
    return { kind: "invited", clinicPatientId };
  };

  const assignDeviceToClinicPatientAPI = async ({
    clinicId,
    clinicPatientId,
    body,
  }: {
    clinicId: number;
    clinicPatientId: number;
    body: AssignDeviceToPatientBody;
  }): Promise<void> => {
    const token = await getAuthenticationToken();
    if (!token) throw new PatientCentricApiError("No auth token", 401, null);
    const url = `${getPatientCentricBaseUrl()}/patient-app/clinic/${clinicId}/patient/${clinicPatientId}/device`;
    const res = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(body),
    });
    const responseBody = await parseJsonSafe(res);
    if (!res.ok) {
      throw new PatientCentricApiError(
        extractMessage(responseBody, `HTTP ${res.status}`),
        res.status,
        responseBody
      );
    }
  };

  /**
   * Fetch the family members currently linked to a patient. Throws
   * PatientCentricApiError on auth/HTTP failure; the service layer funnels
   * that into its standard result shape.
   */
  const listFamilyMembersAPI = async (
    patientId: number
  ): Promise<LinkedFamilyMember[]> => {
    const token = await getAuthenticationToken();
    if (!token) throw new PatientCentricApiError("No auth token", 401, null);
    const url = `${getPatientCentricBaseUrl()}/patient-app/patients/${patientId}/family-members`;
    const res = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
    });
    const body = await parseJsonSafe(res);
    if (!res.ok) {
      throw new PatientCentricApiError(
        extractMessage(body, `HTTP ${res.status}`),
        res.status,
        body
      );
    }
    const members =
      typeof body === "object" &&
      body !== null &&
      Array.isArray((body as { family_members?: unknown }).family_members)
        ? ((body as { family_members: unknown[] }).family_members as unknown[])
        : [];
    return members
      .filter(
        (m): m is Record<string, unknown> => typeof m === "object" && m !== null
      )
      .map((m) => {
        const id = String(m.id ?? "");
        const email = String(m.email ?? "");
        // `name` is never null but may be blank when no real name exists; the
        // backend falls it back to the email, so mirror that here for the rare
        // case both come through empty so the UI always has something to show.
        const name = String(m.name ?? "") || email;
        return { id, name, email };
      })
      // `id` (the Cognito sub) is the stable key, so it's the only required
      // field. Per the API contract `email` can legitimately be "" — filtering
      // on it would wrongly hide members whose user row has no email.
      .filter((m) => Boolean(m.id));
  };

  return {
    fetchAdminClinicsAPI,
    createPatientAppUserAPI,
    createPatientAppUserV2API,
    replacePatientAppUserV2API,
    inviteFamilyMemberAPI,
    listFamilyMembersAPI,
    assignDeviceToClinicPatientAPI,
  };
};
